import axios from 'axios'

import apiClient from '@/lib/api-client'

// Types mirror the backend copilot serializers (T-256 A2/A3).

export type CopilotAccess = { enabled: boolean }

export type ArtifactKind = 'table' | 'chart' | 'list' | 'action_cards' | 'summary'

export type Artifact = {
  id: string
  kind: ArtifactKind
  title: string
  payload: Record<string, unknown>
  evidence: Record<string, unknown>
  created_at: string
}

export type ToolCall = {
  id: string
  tool_name: string
  arguments: Record<string, unknown>
  status: string
  error_class: string
  created_at: string
}

export type MessageRole = 'user' | 'assistant' | 'system'

export type Message = {
  id: string
  role: MessageRole
  content: string
  sequence: number
  status: 'pending' | 'complete' | 'error'
  meta: Record<string, unknown>
  artifacts: Artifact[]
  tool_calls: ToolCall[]
  created_at: string
}

export type Conversation = {
  id: string
  title: string
  status: 'active' | 'archived'
  pinned: boolean
  last_message_at: string | null
  message_count: number
  created_at: string
  updated_at: string
}

export type ConversationDetail = Conversation & { messages: Message[] }

const BASE = '/api/v1/copilot'

export async function fetchAccess(): Promise<CopilotAccess> {
  const { data } = await apiClient.get<CopilotAccess>(`${BASE}/access/`)
  return data
}

export async function listConversations(): Promise<Conversation[]> {
  const { data } = await apiClient.get<Conversation[]>(`${BASE}/conversations/`)
  return data
}

export async function fetchConversation(id: string): Promise<ConversationDetail> {
  const { data } = await apiClient.get<ConversationDetail>(`${BASE}/conversations/${id}/`)
  return data
}

export async function createConversation(title = ''): Promise<ConversationDetail> {
  const { data } = await apiClient.post<ConversationDetail>(`${BASE}/conversations/`, { title })
  return data
}

export async function sendMessage(conversationId: string, content: string): Promise<Message> {
  const { data } = await apiClient.post<Message>(
    `${BASE}/conversations/${conversationId}/messages/`,
    { content },
  )
  return data
}

// Usage-limit signal from the backend (HTTP 429). Not an error state — an expected "you've
// used your allowance today" moment the UI renders as a soft card.
export type QuotaLimitReason = 'daily_limit' | 'token_cap' | 'too_fast'

export type QuotaLimit = {
  message: string
  reason: QuotaLimitReason
  reset_at: string | null
  retry_after: number | null
}

const QUOTA_REASONS: QuotaLimitReason[] = ['daily_limit', 'token_cap', 'too_fast']

export function parseQuotaLimit(error: unknown): QuotaLimit | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 429) return null
  const d = (error.response?.data ?? {}) as Record<string, unknown>
  // Only OUR copilot quota responses carry a known reason code. A different 429 (e.g. the
  // global DRF throttle) has no such reason → fall through to the generic error banner.
  const reason = typeof d.reason === 'string' ? d.reason : ''
  if (!QUOTA_REASONS.includes(reason as QuotaLimitReason)) return null
  return {
    message:
      typeof d.detail === 'string'
        ? d.detail
        : 'Ви використали ліміт AI-Помічника на сьогодні.',
    reason: reason as QuotaLimitReason,
    reset_at: typeof d.reset_at === 'string' ? d.reset_at : null,
    retry_after: typeof d.retry_after === 'number' ? d.retry_after : null,
  }
}

export async function renameConversation(id: string, title: string): Promise<Conversation> {
  const { data } = await apiClient.patch<Conversation>(`${BASE}/conversations/${id}/`, { title })
  return data
}

export async function pinConversation(id: string): Promise<Conversation> {
  const { data } = await apiClient.post<Conversation>(`${BASE}/conversations/${id}/pin/`)
  return data
}

export async function archiveConversation(id: string): Promise<Conversation> {
  const { data } = await apiClient.post<Conversation>(`${BASE}/conversations/${id}/archive/`)
  return data
}

export async function deleteConversation(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/conversations/${id}/`)
}
